/**
 * Helper pur — parse le hash fragment renvoyé par Supabase Auth après
 * redirection sur les flows recovery / invite.
 *
 * Supabase renvoie l'utilisateur sur `/auth/...#access_token=...&refresh_token=...&type=recovery|invite`
 * (ou `#error=...&error_description=...` en cas de lien expiré/invalide).
 *
 * Ce helper :
 *  - extrait `{type, accessToken, refreshToken}` si tout est présent et valide ;
 *  - retourne un `error` machine-readable sinon (pour traduction côté UI).
 *
 * Codes d'erreur retournés (machine-readable, traduits par l'UI via i18n) :
 *  - `parse_failed`     : URL malformée / hash impossible à parser
 *  - `missing_tokens`   : `access_token` ou `refresh_token` absent
 *  - `unknown_type`     : `type` ∉ {recovery, invite}
 *  - `<supabase_code>`  : code passé tel quel par Supabase (`otp_expired`, `access_denied`, etc.)
 */

export type AuthCallbackType = "recovery" | "invite";

export type AuthCallbackResult =
  | {
      type: AuthCallbackType;
      accessToken: string;
      refreshToken: string;
      error: null;
    }
  | {
      type: null;
      accessToken: null;
      refreshToken: null;
      error: string | null;
    };

/**
 * Parse l'URL de callback Supabase.
 *
 * @param url   URL complète (ex. `window.location.href`)
 * @param hash  Hash fragment optionnel (ex. `window.location.hash`). Si omis,
 *              on l'extrait depuis `url`.
 */
export function parseAuthCallbackUrl(url: string, hash?: string): AuthCallbackResult {
  const empty = {
    type: null,
    accessToken: null,
    refreshToken: null,
  } as const;

  let hashStr: string;
  try {
    if (hash !== undefined) {
      hashStr = hash;
    } else {
      const u = new URL(url);
      hashStr = u.hash;
    }
  } catch {
    return { ...empty, error: "parse_failed" };
  }

  // Strip leading '#' s'il y en a un
  const cleaned = hashStr.startsWith("#") ? hashStr.slice(1) : hashStr;

  if (cleaned === "") {
    // Hash vide : pas un callback, pas d'erreur explicite.
    return { ...empty, error: null };
  }

  let params: URLSearchParams;
  try {
    params = new URLSearchParams(cleaned);
  } catch {
    return { ...empty, error: "parse_failed" };
  }

  // Cas erreur explicite renvoyée par Supabase
  const errCode = params.get("error") ?? params.get("error_code");
  if (errCode) {
    return { ...empty, error: errCode };
  }

  const accessToken = params.get("access_token");
  const refreshToken = params.get("refresh_token");
  const type = params.get("type");

  if (!accessToken || !refreshToken) {
    return { ...empty, error: "missing_tokens" };
  }

  if (type !== "recovery" && type !== "invite") {
    return { ...empty, error: "unknown_type" };
  }

  return {
    type,
    accessToken,
    refreshToken,
    error: null,
  };
}
